import { METHOD_REQUIRED_CAPTURES, type AttestationMethod } from '@garnet/domain';
import type { InspectorProfile, InspectorTask } from './types.js';

/**
 * Pure data-shaping for the inspector — no React, no I/O, no clock (CONVENTIONS).
 * Unit-tested directly in shape.test.ts. The capture-completeness gate (PRD §10.6) lives
 * here so it is provable without a renderer.
 */

// ── Method templates → required captures (PRD §10.3) ─────────────────────────
/** The fixed evidence template for a method (domain METHOD_REQUIRED_CAPTURES verbatim). */
export function requiredCaptures(method: AttestationMethod): readonly string[] {
  return METHOD_REQUIRED_CAPTURES[method];
}

export interface CaptureProgress {
  total: number;
  done: number;
  missing: string[];
  complete: boolean;
}

/** How many of the method's required captures are present, and which are still missing. */
export function captureProgress(method: AttestationMethod, captured: ReadonlySet<string>): CaptureProgress {
  const required = requiredCaptures(method);
  const missing = required.filter((c) => !captured.has(c));
  return { total: required.length, done: required.length - missing.length, missing, complete: missing.length === 0 };
}

/**
 * Submission gate (PRD §10.6): an attestation cannot be submitted unless every required
 * capture for its method is present. Pure so the gate is unit-tested directly.
 */
export function canSubmit(method: AttestationMethod, captured: ReadonlySet<string>): boolean {
  return captureProgress(method, captured).complete;
}

// ── Coverage routing (PRD §10.2) ─────────────────────────────────────────────
/**
 * An inspector may only accept a task whose method, jurisdiction and asset class all fall
 * within their verified coverage. Routing excludes out-of-coverage work upstream; the app
 * re-checks so an offered task that drifted out of coverage cannot be accepted.
 */
export function inCoverage(task: InspectorTask, profile: InspectorProfile): boolean {
  return (
    profile.coverage.methods.includes(task.method) &&
    profile.coverage.jurisdictions.includes(task.jurisdiction) &&
    profile.coverage.asset_classes.includes(task.asset_class)
  );
}

// ── Capture-field kinds (UI templating) ──────────────────────────────────────
/** How a required capture is collected on the device. */
export type CaptureKind = 'scan' | 'photo' | 'file' | 'grade' | 'seal' | 'verdict' | 'status' | 'text' | 'check';

const CAPTURE_KIND: Record<string, CaptureKind> = {
  // physical_inspection
  label_photos: 'photo',
  security_feature_photos: 'photo',
  tamper_seal_state: 'seal',
  calibration_record: 'file',
  condition_grade: 'grade',
  udi_carrier_scan: 'scan',
  // as6171_test
  test_plan_id: 'text',
  external_visual: 'photo',
  xray: 'photo',
  xrf: 'file',
  electrical_test_result: 'file',
  as6171_verdict: 'verdict',
  // manufacturer_callback
  serial_range_query: 'text',
  produced_confirmation: 'check',
  sold_to_confirmation: 'check',
  stolen_diverted_status: 'status',
  responder_identity: 'text',
  // document_forensics
  document_images: 'photo',
  issuer_check: 'check',
  date_signature_checks: 'check',
  anomaly_notes: 'text',
};

/** The collection control for a capture key (defaults to a confirm checkbox). */
export function captureKind(key: string): CaptureKind {
  return CAPTURE_KIND[key] ?? 'check';
}

/** Closed option sets for the segmented capture kinds. */
export const CAPTURE_OPTIONS: Record<'grade' | 'seal' | 'verdict' | 'status', readonly string[]> = {
  grade: ['A', 'B', 'C', 'fail'],
  seal: ['intact', 'broken', 'missing'],
  verdict: ['pass', 'fail'],
  status: ['clear', 'stolen', 'diverted'],
};
